import {
  ReactNode,
  useEffect,
  useCallback,
  useMemo,
  useState,
  useRef,
  useContext,
} from 'react';
import {
  capitalize,
  useMediaQuery,
  useTheme,
  Typography,
  ListItem,
  ListItemText,
  Chip,
  ListItemIcon,
  MenuItem,
} from '@mui/material';
import { formatLicensePlateString, logError } from 'utils';
import { useTranslation } from 'react-i18next';
import {
  ActionFunctionArgs,
  useFetcher,
  useLoaderData,
  useNavigate,
  useNavigation,
  useRevalidator,
} from 'react-router';
import { ROUTES, routeWithParams } from 'router';
import { AppContext } from 'context/appContext';
import MobilePanelList from 'components/lists/MobilePanelList';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  MRT_RowVirtualizer,
  MRT_SortingState,
  MRT_Row,
} from 'material-react-table';
import {
  TableType,
  useDefaultTableOptions,
} from 'hooks/useDefaultTableOptions';

import MRTableToolbar from 'components/tables/PanelTableToolbar';
import { ActivityFeedItem, PPIPusherEvent } from 'types/ActivityFeed';
import dayjs from 'dayjs';
import useActiveTenantID from 'hooks/useTenantID';
import { approveJob, denyJob, getActivityFeedEvents, reviewPermit } from 'api';
import Pusher from 'pusher-js';
import {
  EntityTypes,
  Job,
  Permit,
  PermitStatus,
  TagStatus,
  TowStatus,
} from 'types';
import { useFetcherKey } from 'hooks/useFetcherKey';
import { JobActionHandler } from 'pages/jobs/jobsAction';
import { ToastContext, TOAST_TYPE } from 'context/toastContext';
import { PermitTableActionHandler } from 'pages/permits/PermitTable';
import { Approval } from '@mui/icons-material';
import JobReviewDialog from 'pages/jobs/JobReviewDialog';
import PermitReviewDialog from 'pages/permits/PermitReviewDialog';
import { formatLabel } from 'utils';
import { ListItemMenuButton } from 'components/buttons/ListItemMenuButton';
import AuthService from 'utils/AuthService';
type ActivityFeedLoaderParams = {
  params: {
    tenantID: string;
  };
};

type ActivityFeedLoaderResponse = {
  _activityFeed: Promise<ActivityFeedItem[]>;
};

export const activityFeedLoader = async ({
  params,
}: ActivityFeedLoaderParams): Promise<ActivityFeedLoaderResponse> => {
  return { _activityFeed: getActivityFeedEvents(params.tenantID) };
};

export enum ActivityFeedActionHandler {
  APPROVE_JOB = 'approveJob',
  DENY_JOB = 'denyJob',
  APPROVE_ALL_JOBS = 'approveAllJobs',
  APPROVE_PERMIT = 'approvePermit',
  DENY_PERMIT = 'denyPermit',
  APPROVE_ALL_PERMITS = 'approveAllPermits',
  UNKNOWN = 'unknown',
}

export interface ActivityFeedActionResponse {
  status: number;
  statusText: string;
  error?: string;
  handler: ActivityFeedActionHandler;
}

export const activityFeedAction = async ({
  request,
}: ActionFunctionArgs): Promise<ActivityFeedActionResponse> => {
  const {
    handler,
    signature,
    jobID,
    permitID,
    rejectReason,
    tenantID,
    propertyID,
  } = await request.json();

  try {
    switch (handler) {
      case ActivityFeedActionHandler.APPROVE_JOB: {
        if (!jobID || !signature || !tenantID) {
          return {
            status: 400,
            statusText: 'Bad Request',
            error: 'missing required fields',
            handler: handler,
          };
        }
        const approveRes = await approveJob({
          tenantID,
          jobID,
          body: signature,
        });
        if (!approveRes.ok) {
          const json = await approveRes.json();
          return {
            status: approveRes.status,
            statusText: approveRes.statusText,
            error: json.message,
            handler: handler,
          };
        }
        return {
          status: approveRes.status,
          statusText: approveRes.statusText,
          handler: handler,
        };
      }
      case ActivityFeedActionHandler.DENY_JOB: {
        if (!jobID || !rejectReason || !tenantID) {
          return {
            status: 400,
            statusText: 'Bad Request',
            error: 'missing required fields',
            handler: handler,
          };
        }
        const denyRes = await denyJob({
          tenantID,
          jobID,
          reason: rejectReason,
        });
        if (!denyRes.ok) {
          const json = await denyRes.json();
          return {
            status: denyRes.status,
            statusText: denyRes.statusText,
            error: json.message,
            handler: handler,
          };
        }
        return {
          status: denyRes.status,
          statusText: denyRes.statusText,
          handler: handler,
        };
      }
      case ActivityFeedActionHandler.APPROVE_PERMIT:
      case ActivityFeedActionHandler.DENY_PERMIT: {
        if (!permitID || !tenantID || !propertyID) {
          return {
            status: 400,
            statusText: 'Bad Request',
            error: 'missing required fields',
            handler: handler,
          };
        }
        const reviewRes = await reviewPermit({
          tenantID,
          entityType: EntityTypes.Property,
          entityID: propertyID,
          permitID,
          approved: handler === ActivityFeedActionHandler.APPROVE_PERMIT,
        });
        if (!reviewRes.ok) {
          const json = await reviewRes.json();
          return {
            status: reviewRes.status,
            statusText: reviewRes.statusText,
            error: json.message,
            handler: handler,
          };
        }
        return {
          status: reviewRes.status,
          statusText: reviewRes.statusText,
          handler: handler,
        };
      }
      default: {
        return {
          status: 400,
          statusText: 'Bad Request',
          error: 'unknown handler',
          handler: handler,
        };
      }
    }
  } catch (error) {
    return {
      status: 500,
      statusText: 'Internal Server Error',
      error: error instanceof Error ? error.message : 'unknown error',
      handler: handler,
    };
  }
};

const ActivityFeed = (): ReactNode => {
  const { t } = useTranslation();
  const tenantID = useActiveTenantID();
  const { breakpoints } = useTheme();
  const mobile = useMediaQuery(breakpoints.down('lg'));
  const theme = useTheme();
  const navigation = useNavigation();
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [selectedPermit, setSelectedPermit] = useState<Permit | null>(null);
  const [selectedIDs, setSelectedIDs] = useState<number[]>([]);
  const [showDialog, setShowDialog] = useState<'job' | 'permit' | null>(null);
  const displayToast = useContext(ToastContext);
  const { fetcherKey } = useFetcherKey();
  const fetcher = useFetcher({ key: fetcherKey });
  const rowVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null);
  const { state } = useContext(AppContext);
  const revalidator = useRevalidator();
  const { _activityFeed } = useLoaderData<typeof activityFeedLoader>();
  const [activityFeed, setActivityFeed] = useState<ActivityFeedItem[]>([]);

  useEffect(() => {
    const awaitLoader = async () => {
      const activityFeed = await _activityFeed;
      setActivityFeed(activityFeed);
    };
    awaitLoader();
  }, [_activityFeed]);

  useEffect(() => {
    //scroll to the top of the table when the sorting changes
    try {
      rowVirtualizerInstanceRef.current?.scrollToIndex?.(0);
    } catch (error) {
      logError(error as Error);
    }
  }, [sorting]);

  useEffect(() => {
    const pusher = new Pusher(window.PUSHER_KEY, {
      cluster: window.PUSHER_CLUSTER,
    });
    const channel = pusher.subscribe(`tenant-${tenantID}`);
    channel.bind(PPIPusherEvent.ActivityFeed, (e: ActivityFeedItem) => {
      if (!activityFeed.some((item) => item.id === e.id)) {
        revalidator.revalidate();
      }
    });

    return () => {
      channel.unbind(PPIPusherEvent.ActivityFeed);
      pusher.unsubscribe(`tenant-${tenantID}`);
    };
  }, [activityFeed, tenantID]);

  useEffect(() => {
    if (fetcher.data) {
      // if the fetcher data is a job or permit, set the selected job or permit and show the dialog
      if (fetcher.data._job) {
        const awaitJob = async () => {
          const job = await fetcher.data._job;
          setSelectedJob(job);
          setShowDialog('job');
        };
        awaitJob();
        return;
      }

      if (fetcher.data._permit) {
        const awaitPermit = async () => {
          const permit = await fetcher.data._permit;
          setSelectedPermit(permit);
          setShowDialog('permit');
        };
        awaitPermit();
        return;
      }

      const response = fetcher.data as ActivityFeedActionResponse;
      if (response.error) {
        displayToast({
          message: getErrorMessage(response),
          type: TOAST_TYPE.ERROR,
        });
      } else {
        const message = getSuccessMessage(response);
        displayToast({
          message,
          type: TOAST_TYPE.SUCCESS,
        });
        resetState();
      }
    }
  }, [fetcher.data]);

  const getSuccessMessage = useCallback(
    (response: ActivityFeedActionResponse) => {
      switch (response.handler) {
        case ActivityFeedActionHandler.APPROVE_JOB:
          return t('approve_success', {
            ns: 'toasts',
            name: t('job.label', { ns: 'entities' }),
          });
        case ActivityFeedActionHandler.DENY_JOB:
          return t('deny_success', {
            ns: 'toasts',
            name: t('job.label', { ns: 'entities' }),
          });
        case ActivityFeedActionHandler.APPROVE_PERMIT:
          return t('approve_success', {
            ns: 'toasts',
            name: t('permit.label', { ns: 'entities' }),
          });
        case ActivityFeedActionHandler.DENY_PERMIT:
          return t('deny_success', {
            ns: 'toasts',
            name: t('permit.label', { ns: 'entities' }),
          });
        case ActivityFeedActionHandler.APPROVE_ALL_JOBS:
          return t('approve_all_success', {
            ns: 'toasts',
            name: t('job.label', { ns: 'entities' }),
          });
        case ActivityFeedActionHandler.APPROVE_ALL_PERMITS:
          return t('approve_all_success', {
            ns: 'toasts',
            name: t('permit.label', { ns: 'entities' }),
          });
        default:
          return '';
      }
    },
    [t],
  );

  const getErrorMessage = useCallback(
    (response: ActivityFeedActionResponse) => {
      switch (response.handler) {
        case ActivityFeedActionHandler.APPROVE_JOB:
          return t('approve_error', {
            ns: 'toasts',
            name: t('job.label', { ns: 'entities' }),
            error: response.error,
          });
        case ActivityFeedActionHandler.DENY_JOB:
          return t('deny_error', {
            ns: 'toasts',
            name: t('job.label', { ns: 'entities' }),
            error: response.error,
          });
        case ActivityFeedActionHandler.APPROVE_PERMIT:
          return t('approve_error', {
            ns: 'toasts',
            name: t('permit.label', { ns: 'entities' }),
            error: response.error,
          });
        case ActivityFeedActionHandler.DENY_PERMIT:
          return t('deny_error', {
            ns: 'toasts',
            name: t('permit.label', { ns: 'entities' }),
            error: response.error,
          });
        case ActivityFeedActionHandler.APPROVE_ALL_JOBS:
          return t('approve_all_error', {
            ns: 'toasts',
            name: t('job.label', { ns: 'entities' }),
            error: response.error,
          });
        case ActivityFeedActionHandler.APPROVE_ALL_PERMITS:
          return t('approve_all_error', {
            ns: 'toasts',
            name: t('permit.label', { ns: 'entities' }),
            error: response.error,
          });
        default:
          return '';
      }
    },
    [t],
  );

  const resetState = useCallback(() => {
    setSelectedIDs([]);
    setShowDialog(null);
    setSelectedJob(null);
    setSelectedPermit(null);
  }, []);

  const handleItemClick = (row: ActivityFeedItem) => {
    if (row.type === 'permit') {
      navigate(
        routeWithParams(ROUTES.PROPERTY_PERMIT_DETAILS, [
          tenantID,
          row.propertyID,
          row.entityID,
        ]),
      );
    } else {
      navigate(routeWithParams(ROUTES.JOBS_DETAILS, [tenantID, row.entityID]));
    }
  };
  const handleJobApproval = (jobID: number) => {
    fetcher.submit(
      {
        handler: JobActionHandler.APPROVE,
        signature: JSON.stringify(state.signature),
        jobID: jobID,
      },
      {
        method: 'PUT',
        encType: 'application/json',
      },
    );
    setSelectedJob(null);
  };
  const handleJobDeny = (jobID: number, reason: string) => {
    fetcher.submit(
      {
        handler: JobActionHandler.REJECT,
        rejectReason: reason,
        signature: JSON.stringify(state.signature),
      },
      {
        method: 'PUT',
        encType: 'application/json',
      },
    );
    setSelectedJob(null);
  };
  const handlePermitApproval = (permit: Permit) => {
    fetcher.submit(
      {
        handler: ActivityFeedActionHandler.APPROVE_PERMIT,
        propertyID: permit.property?.id || 0,
        tenantID: tenantID,
        permitID: permit.id,
      },
      {
        method: 'PUT',
        encType: 'application/json',
      },
    );
  };
  const handlePermitDenial = (permit: Permit) => {
    fetcher.submit(
      {
        handler: ActivityFeedActionHandler.DENY_PERMIT,
        propertyID: permit.property?.id || 0,
        tenantID: tenantID,
        permitID: permit.id,
      },
      {
        method: 'PUT',
        encType: 'application/json',
      },
    );
  };
  const fetchJobAndLaunchModal = (row: ActivityFeedItem) => {
    fetcher.load(
      routeWithParams(ROUTES.JOBS_DETAILS, [tenantID, row.entityID]),
    );
  };
  const fetchPermitAndLaunchModal = (row: ActivityFeedItem) => {
    fetcher.load(
      routeWithParams(ROUTES.PROPERTY_PERMIT_DETAILS, [
        tenantID,
        row.propertyID,
        row.entityID,
      ]),
    );
  };

  const isReviewable = useCallback(
    (row: ActivityFeedItem) => {
      if (row.type === 'permit') {
        return (
          (row.status === PermitStatus.PendingApproval ||
            row.status === "pending approval") &&
          state.entityPermissions.permit.update
        );
      } else {
        if (
          row.status !== TagStatus.PENDING_APPROVAL &&
          row.status !== TowStatus.PENDING_APPROVAL
        ) {
          return false;
        }

        if (AuthService.IsPM() || AuthService.IsSuperAdmin()) {
          return state.entityPermissions.job.update;
        }
        return false;
      }
    },
    [state.entityPermissions],
  );

  const columns = useMemo<MRT_ColumnDef<ActivityFeedItem>[]>(
    () => [
      {
        header: capitalize(t('columns.property_name', { ns: 'activityFeed' })),
        id: 'propertyName',
        accessorFn: (row) => capitalize(row.propertyName),
      },
      {
        header: capitalize(t('columns.event', { ns: 'activityFeed' })),
        id: 'event',
        accessorFn: (row) =>
          `${capitalize(row.type)} ${row.status
            .split(' ')
            .map(formatLabel)
            .join(' ')}`,
      },
      {
        header: capitalize(t('columns.timestamp', { ns: 'activityFeed' })),
        id: 'timestamp',
        accessorFn: (row) => dayjs(row.timestamp).format('MM/DD/YYYY hh:mm A'),
      },
      {
        header: capitalize(t('columns.contact_name', { ns: 'activityFeed' })),
        id: 'contactName',
        accessorFn: (row) => capitalize(row.contactName),
      },
      {
        header: capitalize(t('columns.license_plate', { ns: 'activityFeed' })),
        id: 'licensePlate',
        accessorFn: (row) => formatLicensePlateString(row.plate),
      },
      {
        header: capitalize(t('columns.type', { ns: 'activityFeed' })),
        accessorKey: 'type',
        Cell: ({ row }: { row: MRT_Row<ActivityFeedItem> }) => {
          return (
            <Chip
              label={capitalize(row.original.type)}
              color={'primary'}
              variant={'outlined'}
              sx={{
                width: 100,
                color: theme.palette.primary.main,
                fontWeight: 500,
              }}
            />
          );
        },
      },
    ],
    [],
  );

  const tableDefaults = useDefaultTableOptions<ActivityFeedItem>(
    TableType.PANEL,
  );

  const table = useMaterialReactTable({
    ...tableDefaults,
    data: activityFeed || [],
    columns,
    enableRowVirtualization: true,
    enableRowSelection: false,
    enableRowActions: true,
    enableColumnActions: false,
    enableColumnOrdering: false,
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: true,
      density: 'comfortable',
      columnVisibility: {
        propertyName: true,
        event: true,
        timestamp: true,
        contactName: true,
        licensePlate: true,
        type: true,
        status: true,
        mrtRowActions: true,
      },
      columnPinning: {
        right: ['mrt-row-actions'],
      },
    },
    state: {
      sorting,
      showLoadingOverlay: false,
      isLoading: navigation.state === 'loading',
    },
    onSortingChange: setSorting,
    getRowId: (row) => `${row.id}`,
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: 'white',
        cursor: 'pointer',
      },
      onClick: () => handleItemClick(row.original),
    }),
    renderRowActionMenuItems: ({ closeMenu, row }) => [
      <MenuItem
        key={1}
        divider
        onClick={() => {
          closeMenu();
          if (row.original.type === 'permit') {
            fetchPermitAndLaunchModal(row.original);
          } else {
            fetchJobAndLaunchModal(row.original);
          }
        }}
        sx={{ m: 0, backgroundColor: 'white' }}
        disabled={!isReviewable(row.original)}
      >
        <ListItemIcon>
          <Approval />
        </ListItemIcon>
        {`${t('review')} ${capitalize(row.original.type)}`}
      </MenuItem>,
    ],
    renderTopToolbar: ({ table }) => (
      <MRTableToolbar
        table={table}
        optionsDisabled={true}
        tableType={TableType.PANEL}
      />
    ),
  });

  if (mobile) {
    const itemHeight = 70;
    const renderItem = useCallback(
      (row: ActivityFeedItem, index: number): ReactNode => (
        <ListItem
          key={`property-${row.entityID}-${index}`}
          onClick={() => handleItemClick(row)}
          divider
          sx={{ height: itemHeight, backgroundColor: 'white' }}
        >
          <ListItemText
            primary={
              <Typography variant='subtitle1' noWrap>
                {row.propertyName}
              </Typography>
            }
            secondary={
              <Typography variant='subtitle2'>
                {`${capitalize(row.type)} ${formatLabel(row.status)} - ${dayjs(row.timestamp).format('MM/DD/YYYY hh:mm A')}`}
              </Typography>
            }
          />
          <ListItemMenuButton
            menuOptions={[
              {
                content: t('review'),
                onClick: () => {
                  if (row.type === 'permit') {
                    fetchPermitAndLaunchModal(row);
                  } else {
                    fetchJobAndLaunchModal(row);
                  }
                },
                disabled: !isReviewable(row),
              },
            ]}
          />
        </ListItem>
      ),
      [activityFeed || []],
    );

    return (
      <MobilePanelList
        data={activityFeed || []}
        columns={columns.map((c) => ({
          name: c.header,
          searchKey: c.accessorKey,
        }))}
        renderItem={renderItem}
        itemHeight={70}
        title={t('title', { ns: 'activityFeed' })}
        withSearchBar
      />
    );
  }

  return (
    <>
      {showDialog === 'job' && selectedJob && (
        <JobReviewDialog
          job={selectedJob}
          isOpen={showDialog === 'job'}
          handleCancel={() => setShowDialog(null)}
          handleJobApproval={handleJobApproval}
          handleJobDeny={handleJobDeny}
        />
      )}
      {showDialog === 'permit' && selectedPermit && (
        <PermitReviewDialog
          permit={selectedPermit}
          isOpen={showDialog === 'permit'}
          handleCancel={() => setShowDialog(null)}
          handlePermitApproval={handlePermitApproval}
          handlePermitDenial={handlePermitDenial}
        />
      )}
      <MaterialReactTable table={table} />
    </>
  );
};

export default ActivityFeed;
